import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router';
import {
  AddPaymentView,
  YourDetailsView,
  WorldpayUnreachable,
  NoLongerAvailableView,
} from './views';
import { PaymentView, AddressView } from '@vehicle_enquiry/reserve_now/views';
import ProgressDisplay from '@components/application/progressDisplay';
import request from 'superagent';
import Analytics from '@lib/analytics';
import { snakeCaseKeys, toQueryString } from '@javascript/lib/utils';
import { Vehicle } from '@sharedTypes/Vehicle';
import { Enquiry } from '@sharedTypes/Enquiry';

export interface AddPaymentContainerProps {
  authenticityToken: string;
  customerLocation: string;
  orderBuilderToken: string;
  vehicle: Vehicle;
  worldpayHelper: string;
}

const AddPaymentContainer = ({
  authenticityToken,
  customerLocation,
  orderBuilderToken,
  vehicle,
  worldpayHelper,
}: AddPaymentContainerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState<Enquiry>({
    customerDetails: {
      comments: '',
      contact_email: '',
      contact_number: '',
      first_name: '',
      last_name: '',
      address1: '',
      address2: '',
      city: '',
      shopper_id: '',
      state: { value: '' },
      postcode: '',
      worldpay_url: '',
    },
  });
  const { customerDetails } = enquiry;
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const generateRoute = (routeName: string) =>
    `${routeName}/${vehicle.stockReference}/${orderBuilderToken}`;

  const steps = [
    {
      name: 'Add Payment',
      route: generateRoute('/new'),
    },
    {
      name: 'Your Contact Details',
      route: generateRoute('/your-details'),
    },
    {
      name: 'Your Address',
      route: generateRoute('/address'),
    },
    {
      name: 'Your Payment Details',
      route: generateRoute('/payment'),
    },
    {
      name: 'Worldpay Unreachable',
      route: generateRoute('/payment-unavailable'),
    },
    {
      name: 'No longer available',
      route: generateRoute('/no-longer-available'),
    },
  ];

  const stepIndex = steps.findIndex(
    (step) => step.route.toUpperCase() === location.pathname.toUpperCase()
  );

  const nextView = () => {
    navigate(steps[stepIndex + 1].route);
  };

  const setStep = (route: string) => {
    navigate(generateRoute(route));
  };

  useEffect(() => {
    if (!vehicle?.isReserved) return;

    setError(true);
    setStep('/no-longer-available');
  }, []);

  const updateEnquiry = (
    details: object,
    callback?: (customerDetails: object) => void
  ) => {
    setEnquiry((prevState) => {
      return {
        ...prevState,
        customerDetails: { ...prevState.customerDetails, ...details },
      };
    });

    if (callback)
      callback({
        ...enquiry,
        customerDetails: { ...enquiry.customerDetails, ...details },
      });
  };

  const checkReserved = () => {
    request
      .get(`/api/reservation/${vehicle.stockReference}`)
      .then((response) => {
        if (!response.body?.reserved) return;

        setError(true);
        setStep('/no-longer-available');
      })
      .catch((err) => {
        if (!err) return;

        setError(true);
        setStep('/no-longer-available');
      });
  };

  const redirectToThankYouPage = () => {
    const params = toQueryString({
      shopper_id: enquiry.customerDetails.shopper_id,
      stock_reference: vehicle.stockReference,
      token: orderBuilderToken,
    });

    Analytics.trackEvent('conversion', {
      action: 'Created reservation',
      label: 'Add payment',
    });
    window.location.href = `/contact-us/thank-you/deal-builder/reserved?${params}`;
  };

  const redirectToErrorPage = () => {
    window.location.href = `/contact-us/thank-you/deal-builder/error?stock_reference=${vehicle.stockReference}`;
  };

  const prepareEnquiry = (enquiry: Enquiry) => {
    return {
      ...snakeCaseKeys(enquiry.customerDetails),
    };
  };

  const sendEnquiry = (enquiry: object) => {
    setLoading(true);
    request
      .post(
        `/contact-us/vehicle-enquiry/create-reservation/${vehicle.stockReference}`
      )
      .send({
        authenticity_token: authenticityToken,
        order_builder_token: orderBuilderToken,
        reserve_now: true,
        enquiry: prepareEnquiry(enquiry as Enquiry),
      })
      .then((response) => {
        setLoading(false);
        if (response.ok && response.body.url) {
          updateEnquiry({
            shopper_id: response.body.shopper_id,
            worldpay_url: response.body.url,
          });
          setStep('/payment');
        } else {
          setStep('/payment-unavailable');
        }
      })
      .catch((err) => {
        if (window.newrelic) {
          window.newrelic.noticeError(err);
        }
        return redirectToErrorPage();
      });
  };

  const exitReserveJourney = () => {
    window.location.href = vehicle.url;
  };

  return (
    <>
      <div className="ch-bg--white reserve-now__header">
        <button
          className="ac-exit-button ch-ba--0 ch-float--right ch-color--ac-black ch-bg--grey-2 ch-rounded--sm ch-fs--2 ch-fw--500 ch-ma--4"
          onClick={exitReserveJourney}
          type="button"
        >
          Exit
        </button>
      </div>
      {!error && (
        <ProgressDisplay
          currentStep={stepIndex + 1}
          stepName={steps[stepIndex].name}
          stepped
          totalSteps={4}
        />
      )}
      <div className="db-body">
        <Routes>
          <Route
            element={<AddPaymentView nextView={nextView} vehicle={vehicle} />}
            path={generateRoute('/new')}
          />
          <Route
            element={
              <YourDetailsView
                customerDetails={customerDetails}
                nextView={nextView}
                updateEnquiry={updateEnquiry}
              />
            }
            path={generateRoute('/your-details')}
          />
          <Route
            element={
              <AddressView
                customerDetails={customerDetails}
                customerLocation={customerLocation}
                loading={loading}
                sendEnquiry={sendEnquiry}
                updateCustomerDetails={updateEnquiry}
              />
            }
            path={generateRoute('/address')}
          />
          <Route
            element={
              <PaymentView
                checkReserved={checkReserved}
                completeEnquiry={redirectToThankYouPage}
                enquiry={enquiry}
                orderBuilderToken={orderBuilderToken}
                stockReference={vehicle.stockReference}
                worldpayHelper={worldpayHelper}
              />
            }
            path={generateRoute('/payment')}
          />

          <Route
            element={<WorldpayUnreachable />}
            path={generateRoute('/payment-unavailable')}
          />

          <Route
            element={<NoLongerAvailableView vehicle={vehicle} />}
            path={generateRoute('/no-longer-available')}
          />
        </Routes>
      </div>
    </>
  );
};

export default AddPaymentContainer;
